import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, selectUserLoggedIn } from '../../modules';
import {
    selectOrderLoading,
    selectStockMatchData,
    selectStockMatchLoading,
} from '../../modules/user/stock-order/selector';
import { Button, Empty, InputNumber, Spin } from 'antd';
import { AntdTable } from '../../components/AntdTable';
import { selectUserAssetsData, selectUserAssetsDataLoading } from '../../modules/user/assets/selector';
import { selectListPriceBoard, selectListPriceBoardLoading } from '../../modules/public/listPriceBoard/selector';
import { formatNumber } from '../../helpers/formatNumber';
import debounce from 'lodash.debounce';
import { AntdModal } from '../../components/AntdModal';
import { fetchOrderRequest, OrderPayload } from '../../modules/user/stock-order/actions';
import { selectCurrentTicker } from '../../modules/public/tickers/selector';
import { formatDate } from '../../helpers';
import { useHistory } from 'react-router';

export const DEBOUNCE_TIME = 300;
export const AMOUNT_STEP = 100;
export const initOrderState = { price: 0, totalAmount: 0, total: 0 };

export const CreateOrderCombined = () => {
    const isLogin = useSelector((state: RootState) => selectUserLoggedIn(state));
    const matchData = useSelector((state: RootState) => selectStockMatchData(state));
    const loading = useSelector((state: RootState) => selectStockMatchLoading(state));
    const priceBoard = useSelector((state: RootState) => selectListPriceBoard(state));
    const listPriceBoardLoading = useSelector((state: RootState) => selectListPriceBoardLoading(state));
    const userAssets = useSelector((state: RootState) => selectUserAssetsData(state));
    const userAssetsLoading = useSelector((state: RootState) => selectUserAssetsDataLoading(state));
    const createOrderLoading = userAssetsLoading && listPriceBoardLoading;
    const orderRequestLoading = useSelector((state: RootState) => selectOrderLoading(state));
    const currentTicker = useSelector((state: RootState) => selectCurrentTicker(state));
    const currentPorfolios = userAssets?.portfolios?.find((item) => item?.ticker === currentTicker?.ticker);
    const dispatch = useDispatch();
    const history = useHistory();

    const [orderType, setOrderType] = useState('limit');
    const [orderState, setOrderState] = useState(initOrderState);
    const [errorMessage, setErrorMessage] = useState('');
    const [openCancelModal, setOpenCancelModal] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [orderAction, setOrderAction] = useState('');

    const userBalance = Number(userAssets?.wallet?.[0]?.balance);
    const isDisabled =
        orderType === 'limit'
            ? !!errorMessage || !orderState.price || !orderState.totalAmount || !isLogin
            : !!errorMessage || !orderState.totalAmount || !isLogin;

    const handleChangeOrderState = (key: string, value: number) => {
        setOrderState((prev) => {
            const updatedState = { ...prev, [key]: value };
            setErrorMessage('');
            if (!isLogin) {
                setErrorMessage(`Please login`);
            }
            if (updatedState.totalAmount) {
                if (updatedState.totalAmount % AMOUNT_STEP !== 0) {
                    setErrorMessage(`Please enter an amount that is a multiple of ${AMOUNT_STEP}.`);
                }
                if (updatedState.totalAmount < AMOUNT_STEP) {
                    setErrorMessage(`Min amount is ${AMOUNT_STEP}`);
                }
            }

            if (updatedState.price && updatedState.totalAmount) {
                const total = updatedState.price * updatedState.totalAmount;
                updatedState.total = total;
            }

            return updatedState;
        });
    };

    const debouncedHandleChangeOrderState = debounce(handleChangeOrderState, DEBOUNCE_TIME);

    const columns = [
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (value: any) => {
                return Number(value).toLocaleString();
            },
        },
        {
            title: 'Price',
            dataIndex: 'price',
            key: 'price',
            render: (value: any) => {
                return Number(value).toLocaleString();
            },
        },
        {
            title: 'Time',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (text: any) => formatDate(text, 'time'),
        },
        {
            title: 'Side',
            dataIndex: 'side',
            key: 'side',
            render: (text: any) => (
                <span
                    style={{
                        textTransform: 'capitalize',
                        color: text === 'buy' ? 'rgb(58, 204, 138)' : 'rgb(223, 95, 103)',
                    }}>
                    {text}
                </span>
            ),
        },
    ];

    const handleChangeOrderType = (type: string) => {
        setOrderType(type);
        setOrderState(initOrderState);
        setErrorMessage('');
    };

    // Function to open the modal when the user clicks "Buy" or "Sell"
    const handleOrderConfirmation = (action: string) => {
        setOrderAction(action);
        setModalTitle(action === 'buy' ? 'Confirm Buy Order' : 'Confirm Sell Order');

        // Format the order details for the modal
        const amountText = orderState.totalAmount ? `Amount: ${orderState.totalAmount}` : '';
        const priceText = orderState.price ? `Price: ${formatNumber(orderState.price)}` : '';

        // Set up the confirmation message to include amount and price if limit order
        const confirmationMessage =
            orderType === 'limit' ? `${amountText}\n${priceText}` : `Amount: ${orderState.totalAmount}`;

        setOpenCancelModal(true);
        setModalTitle(confirmationMessage);
    };

    const handleOrder = () => {
        dispatch(
            fetchOrderRequest({
                type: orderType,
                ticker: currentTicker.ticker,
                totalAmount: orderState.totalAmount,
                side: orderAction,
                price: orderState.price,
            } as OrderPayload),
        );
    };

    // Close modal when order request is done (loading is false)
    useEffect(() => {
        if (!orderRequestLoading) {
            setOrderState(initOrderState);
            setOpenCancelModal(false); // Close the modal when loading is complete
        }
    }, [orderRequestLoading]);

    return (
        <div className="desktop-create-order-combined">
            <div className="desktop-create-order-combined_match">
                {loading ? (
                    <Spin />
                ) : matchData?.length === 0 ? (
                    <Empty style={{ backgroundColor: 'var(--main-background-color)' }} />
                ) : (
                    <AntdTable
                        title={() => 'Order matching'}
                        columns={columns}
                        dataSource={matchData || []}
                        rowKey="id"
                        pagination={false}
                    />
                )}
            </div>

            <div className="desktop-create-order-combined_create-order">
                {!createOrderLoading ? (
                    <>
                        <div className="desktop-create-order-combined_create-order_title">
                            <div
                                onClick={() => {
                                    handleChangeOrderType('limit');
                                }}
                                className={`desktop-create-order-combined_create-order_title__limit ${orderType === 'limit' && 'active'}`}>
                                Limit
                            </div>
                            <div
                                onClick={() => handleChangeOrderType('market')}
                                className={`desktop-create-order-combined_create-order_title__market  ${orderType === 'market' && 'active'}`}>
                                Market
                            </div>
                        </div>
                        <div className="desktop-create-order-combined_create-order_body">
                            {isLogin && (
                                <div className="desktop-create-order-combined_create-order_body__avai-balance">
                                    <p className="desktop-create-order-combined_create-order_body__avai-balance__label">
                                        Available Balance
                                    </p>
                                    <div>
                                        <span>
                                            {' '}
                                            {currentPorfolios?.amount
                                                ? Number(currentPorfolios?.amount).toLocaleString()
                                                : 0}{' '}
                                            shares
                                        </span>
                                        <span>
                                            {userAssets?.wallet?.[0]?.balance ? (
                                                <p>{Number(userAssets?.wallet?.[0]?.balance).toLocaleString()}đ</p>
                                            ) : (
                                                0
                                            )}
                                        </span>
                                    </div>
                                </div>
                            )}
                            <div className="desktop-create-order-combined_create-order_body__prices">
                                <div className="desktop-create-order-combined_create-order_body__prices--lable">
                                    Prices
                                </div>
                                <div className="desktop-create-order-combined_create-order_body__prices--list">
                                    <p
                                        onClick={() => handleChangeOrderState('price', +priceBoard[0]?.ceiling)}
                                        style={{ color: 'var(--ceil-color)' }}>
                                        {formatNumber(priceBoard[0]?.ceiling)}
                                    </p>
                                    <p
                                        onClick={() => handleChangeOrderState('price', +priceBoard[0]?.ref_price)}
                                        style={{ color: 'var(--ref-color)' }}>
                                        {formatNumber(priceBoard[0]?.ref_price)}
                                    </p>
                                    <p
                                        onClick={() => handleChangeOrderState('price', +priceBoard[0]?.floor)}
                                        style={{ color: 'var(--floor-color)' }}>
                                        {formatNumber(priceBoard[0]?.floor)}
                                    </p>
                                </div>
                            </div>
                            <div className="desktop-create-order-combined_create-order_body__inputs">
                                <div className="input-container">
                                    <p>Amount</p>
                                    <InputNumber
                                        onChange={(value) =>
                                            debouncedHandleChangeOrderState('totalAmount', value as number)
                                        }
                                        className="amount"
                                        step={AMOUNT_STEP}
                                        value={!!orderState.totalAmount ? orderState.totalAmount : ''}
                                    />
                                </div>

                                {orderType === 'limit' && (
                                    <div className="input-container">
                                        <p>Price</p>
                                        <InputNumber
                                            value={!!orderState.price ? orderState.price : ''}
                                            onChange={(value) =>
                                                debouncedHandleChangeOrderState('price', value as number)
                                            }
                                            className="price"
                                            min={Number(priceBoard[0]?.floor)}
                                            max={Number(formatNumber(priceBoard[0]?.ceiling))}
                                        />
                                    </div>
                                )}
                                {orderType === 'limit' && !!orderState.total && (
                                    <div className="total-box">
                                        <p>Total:{formatNumber(orderState.total)} </p>
                                    </div>
                                )}
                            </div>
                            <div
                                className="desktop-create-order-combined_create-order_body__buttons"
                                style={!!orderState.total ? { marginTop: '5px' } : { marginTop: '20px' }}>
                                {isLogin ? <>
                                    <Button
                                        className="desktop-create-order-combined_create-order_body__buttons--sell"
                                        disabled={isDisabled}
                                        onClick={() => handleOrderConfirmation('sell')}>
                                        Sell
                                    </Button>
                                    <Button
                                        className="desktop-create-order-combined_create-order_body__buttons--buy"
                                        disabled={isDisabled}
                                        onClick={() => handleOrderConfirmation('buy')}>
                                        Buy
                                    </Button> </> : <Button
                                        className="desktop-create-order-combined_create-order_body__buttons--login"
                                        onClick={() => history.push('/signin')}
                                    >   
                                    Register/Login
                                </Button>}

                            </div>
                            <div className="desktop-create-order-combined_create-order_body__error">
                                <p>{errorMessage}</p>
                            </div>
                        </div>
                    </>
                ) : (
                    <Spin />
                )}
                <AntdModal
                    title={`Are you sure you want to ${orderAction}?`}
                    open={openCancelModal}
                    onCancel={() => setOpenCancelModal(false)}
                    onOk={handleOrder}
                    loading={orderRequestLoading}>
                    <p>{modalTitle}</p>
                </AntdModal>
            </div>
        </div>
    );
};
